import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountChip } from '@src/components/AccountChip/AccountChip';
import { NotificationBadge } from '@src/components/NotificationBadge/NotificationBadge';
import { BellIcon } from '@src/components/icons/BellIcon';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { Button, Typography } from '@src/components';
import { useNotifications } from './NotificationsContext';
import { NotificationsList } from './NotificationsList';

export function NotificationsBell() {
  const { unread } = useNotifications();
  return (
    <span className="notifications-bell">
      <BellIcon />
      <NotificationBadge count={unread} testId="notifications-count" />
    </span>
  );
}

export function NotificationsMenu() {
  const { t } = useTranslation();
  const { unread, refresh } = useNotifications();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  useEffect(() => {
    if (!open) return;
    closeButton.current?.focus();
    const outside = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  return (
    <div ref={root} className="notifications-menu">
      <button
        ref={trigger}
        type="button"
        className="notifications-menu__trigger"
        aria-label={
          unread ? t('notifications.withUnread', { count: unread }) : t('notifications.title')
        }
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          setOpen(!open);
          if (!open) void refresh();
        }}
        data-testid="header-notifications"
      >
        <AccountChip icon={<NotificationsBell />} labelHidden>
          {t('notifications.title')}
        </AccountChip>
      </button>
      {open && (
        <section
          id={panelId}
          className="notifications-popover"
          aria-label={t('notifications.title')}
          data-testid="notifications-popover"
        >
          <div className="notifications-popover__header">
            <Typography as="h2" role="title/card">
              {t('notifications.title')}
            </Typography>
            <Button
              ref={closeButton}
              variant="ghost"
              size="small"
              icon={<CloseIcon />}
              aria-label={t('header.close')}
              onClick={() => {
                setOpen(false);
                trigger.current?.focus();
              }}
            />
          </div>
          <NotificationsList onNavigate={() => setOpen(false)} />
        </section>
      )}
    </div>
  );
}
